/**
 * 기록 출처 (TASK-2026-004 R2-REV-001).
 *
 * ## 문제
 *
 * 계측 로그·진행 중 시도 checkpoint는 origin 하나의 저장소 키(`ccatlas.telemetry`,
 * `ccatlas.attempt`)에 쌓였다. 같은 브라우저의 여러 탭 —— 캠페인 없이 연습 중인 탭,
 * 완료된 앞선 세션의 탭, 새 참가 세션의 탭 —— 이 **같은 배열**에 이어 썼다. 세션을
 * 만든 시각에 순번 하나로 경계를 그어도, 열린 채 계속 기록하는 연습 탭의 기록은 경계
 * 뒤에 들어와 새 세션의 자료처럼 보였다.
 *
 * ## 결정
 *
 * 출처를 **기록이 만들어지는 순간의 저장 키**에 새긴다. 참가 세션의 기록은
 * `ccatlas.session.<session_ref>.` 접두가 붙은 키에만 쓰고, 연습 기록은 원래 키에 쓴다.
 * 탭마다 자기가 어느 출처로 기록하는지 알고 있으므로(연습 페이지·확인된 세션), 다른
 * 탭이 무엇을 쓰든 세션 로그에는 그 세션의 기록만 있다. 순번도 세션 로그 안에서 1부터
 * 이어지므로 다시 매길 필요가 없다. 기존 기록은 지우거나 고치지 않는다.
 */

export const SESSION_KEY_PREFIX = 'ccatlas.session.';

/** 저장 키에 넣을 수 있는 세션 참조. 서버가 발급하는 모양보다 넓고, 키 구분자는 막는다. */
const SESSION_REF_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function isScopableSessionRef(sessionRef: string): boolean {
  return SESSION_REF_PATTERN.test(sessionRef);
}

/**
 * 세션 키 공간으로 옮긴 저장소.
 *
 * 로그·checkpoint 모듈은 키 이름을 그대로 쓰고, 이 층이 접두를 붙인다. 그래서 저장
 * 확인 기록의 `storage_key` 같은 논리 이름과 저장 방식은 연습과 세션이 같다.
 */
export function scopedStorage(storage: KeyValueStorage, sessionRef: string): KeyValueStorage {
  if (!isScopableSessionRef(sessionRef)) {
    throw new Error('세션 참조를 저장 키로 쓸 수 없습니다.');
  }
  const prefix = `${SESSION_KEY_PREFIX}${sessionRef}.`;
  return {
    getItem: (key) => storage.getItem(`${prefix}${key}`),
    setItem: (key, value) => {
      storage.setItem(`${prefix}${key}`, value);
    },
    removeItem: (key) => {
      storage.removeItem(`${prefix}${key}`);
    },
  };
}

let current: string | undefined;

/**
 * 이 페이지가 지금 기록하는 출처. `undefined`면 연습(로컬)이다.
 *
 * 참가 세션을 서버에서 확인했을 때만 바꾼다. 확인 전의 기록은 연습 쪽에 남는다.
 */
export function recordingSessionRef(): string | undefined {
  return current;
}

export function setRecordingSession(sessionRef: string | undefined): void {
  if (sessionRef !== undefined && !isScopableSessionRef(sessionRef)) {
    throw new Error('세션 참조를 저장 키로 쓸 수 없습니다.');
  }
  current = sessionRef;
}

let paused = false;

/**
 * 이 페이지의 기록을 멈춘다 (TASK-2026-012 CR-03).
 *
 * 뒤로가기로 되살아났거나 숨었다 돌아온 참가 화면은 서버에 이 참여가 아직 열려 있는지
 * 다시 묻는다. 그 답을 받기 전에 남는 기록 —— 판을 내리는 정리 effect의 시도 종료, 연출
 * 타이머 —— 은 이미 완료된 세션의 꼬리가 될 수 있다. 그래서 묻기 **전에** 동기적으로 멈추고,
 * 같은 참여가 열려 있다고 확인된 뒤에만 다시 연다. 멈춘 동안의 기록은 저장하지 않는다.
 * 그동안 화면은 입력을 받지 않으므로 참가자가 만든 행동이 사라지지는 않는다.
 */
export function pauseRecording(): void {
  paused = true;
}

export function resumeRecording(): void {
  paused = false;
}

export function recordingPaused(): boolean {
  return paused;
}
